<template>
<sidebar-widget
    :start-expanded="true"
    :sections="sections"
    :headers="['Position', '']"
>
    <template v-slot:title>Job Information</template>

    <template v-slot:headerbutton>
        <button class="btn btn-outline-primary" @click.prevent.stop="addModalOpen = true"><i class="far fa-plus"></i> Add</button>
    </template>

    <template v-slot:row-details="props">
        <job-details
            :is-mutation="false"
            :job="props.row.job"
            :earning-type-lookup="earningTypeLookup"
        />

        <button-row>
            <button class="btn btn-outline-primary" @click.prevent="removeJob(props.row.job.id)"><i class="far fa-trash"></i> Delete</button>

            <button class="btn btn-outline-primary" @click.prevent="editJob(props.row.job.id)"><i class="far fa-pen"></i> Edit</button>
        </button-row>
    </template>

    <template v-slot:subrow="props">
        <section v-if="Object.keys(pendingChanges[props.row.job.id] || []).length" class="ml-n2 mr-n2">
            <table class="table css-table wdiget-table table-fixed" >

                <tbody v-for="pc in pendingChanges[props.row.job.id]" class="pending-change">
                    <tr>
                        <td class="cw-toggle position-relative pl-1">
                            <a href class="widget-toggle-wrapper collapse-trigger" role="button" :class="{'is-expanded': expandedItems[pc.id]}" @click.prevent="expandedItems[pc.id] = !expandedItems[pc.id]">
                                <collapse-toggle-button />
                            </a>
                        </td>

                        <td class="truncate-wrapper pl-0">
                            <div class="text-italic mb-0">Pending Change effective {{ $filters.date(pc.effective_on) }}</div>

                            <div>{{ pc.summary }}</div>
                        </td>
                    </tr>

                    <tr v-show="expandedItems[pc.id]">
                        <td colspan="2" class="pl-1 border-0">
                            <div class="card card-body item-details mb-2">
                                <section class="item-details-section">
                                    <job-details
                                        :is-mutation="true"
                                        :job="pc.job"
                                        :diff="pc.diff"
                                        :earning-type-lookup="earningTypeLookup"
                                    />

                                    <button-row>
                                        <button class="btn btn-outline-primary" @click.prevent="removeMutation(pc.job.id)"><i class="far fa-trash"></i> Cancel</button>
                                    </button-row>

                                </section>
                            </div>
                        </td>
                    </tr>

                </tbody>

            </table>
        </section>
    </template>

    <template v-slot:extra>
        <jobs-modal
            v-if="addModalOpen || editModalOpen"
            :job="currentJob"
            :client="client"
            :employee="employee"
            :future-mutations="jobMutations"
            :client-earning-types="clientEarningTypes"
            :positions="positions"
            @close="currentMutationId = currentJobId = null; addModalOpen = editModalOpen = false"
            @updated="$emit('updated'); getData()"
        />
    </template>
</sidebar-widget>

</template>

<script>
import SidebarWidget from '@/components/SidebarWidget'
import ButtonRow from '@/components/ButtonRow'
import JobsModal from './JobsModal'
import JobDetails from './JobDetails'
import moment from 'moment'


export default {
    props: ['employee', 'client', 'clientEarningTypes', 'positions', ],
    components: {SidebarWidget, ButtonRow, JobsModal, JobDetails, },
    computed: {
        earningTypeLookup() {
            const result = {}

            this.clientEarningTypes.forEach(cet => {
                result[cet.slug] = cet
            })

            return result
        },
        sections() {
            const currentRows = []

            this.employee.jobs.filter(j => {
                return (j.is_active)
            }).forEach(j => {
                const unitMap = {H: 'hour', S: 'pay period', P: 'unit'}
                const rateMap = {H: j.hourly_rate, S: j.salary_rate, P: j.unit_rate}
                const unit = unitMap[j.pay_type]
                const position = this.positions.find(p => p.id == j.position_id)

                if (!position) {
                    return
                }

                const rate = rateMap[j.pay_type]
                let summary = `${this.$filters.dollars(rate)}/${unit}`
                if (j.additional_compensation.length) {
                    summary += ' with additional compensation'
                }

                currentRows.push({
                    job: j,
                    cells: [
                        position.terminal_value,
                        summary,
                    ],
                    id: j.id,
                })
            })
            return [
                {title: 'Current', rows: currentRows, startExpanded: true},
            ]
        },
        pendingChanges() {
            const pendingChanges = {}

            const mutations = this.jobMutations.filter(m => !m.is_applied && m.is_active)
            mutations.sort((a, b) => a.effective_on.localeCompare(b.effective_on))

            const mutationsByJob = {}

            mutations.forEach((m, idx) => {
                if (!mutationsByJob[m.job_id]) {
                    mutationsByJob[m.job_id] = []
                }

                mutationsByJob[m.job_id].push(m)
            })


            Object.keys(mutationsByJob).forEach(jobId => {
                const jobMutations = mutationsByJob[jobId]

                jobMutations.forEach((m, idx) => {
                    const unit = (m.pay_type == 'salary') ? 'pay period' : 'hour'
                    const position = this.positions.find(p => p.id == m.position_id)

                    if (!position) {
                        return
                    }

                    const prev = (idx == 0) ? this.employee.jobs.find(j => j.id == m.job_id) : mutations[idx - 1]
                    const diff = this.computeDiff(m, prev)

                    if (!pendingChanges[m.job_id]) {
                        pendingChanges[m.job_id] = []
                    }

                    pendingChanges[m.job_id].push({
                        job: m,
                        prev: prev,
                        diff: diff,
                        effective_on: m.effective_on,
                        terminal_value: position.terminal_value,
                        summary: this.computeDiffLine(diff),
                        id: m.id,
                    })
                })
            })

            return pendingChanges
        },
        currentJob() {
            if (this.currentJobId) {
                return this.employee.jobs.find(j => j.id == this.currentJobId)
            }
            if (this.currentMutationId) {
                return this.jobMutations.find(m => m.id == this.currentMutationId)
            }
        },
    },
    data() {
        return {
            addModalOpen: false,
            editModalOpen: false,
            removeModalOpen: false,
            currentJobId: null,
            currentMutationId: null,
            jobMutations: [],

            expandedItems: {},
        }
    },
    mounted() {
        this.getData(true)
    },
    methods: {
        removeMutation(mutationId) {
            const mutation = this.jobMutations.find(m => m.id == mutationId)
            if (!mutation) {return}

            if (confirm('Are you sure?')) {
                this.$store.dispatch('START_LOADING')
                return this.$api.del(`/clients/${this.client.id}/employees/${this.employee.id}/job-mutations/${mutation.id}`).then(resp => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showInfo('Successfully deleted job information.')
                    this.$emit('updated')
                    this.getData()
                }).catch(errors => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.__all__)
                })
            }
        },
        removeJob(jobId) {
            if (confirm('Are you sure?')) {
                this.$store.dispatch('START_LOADING')
                const data = {
                    job_id: jobId,
                    effective_on: moment().format('YYYY-MM-DD'),
                    active_status: 'inactive',
                }

                return this.$api.post(`/clients/${this.client.id}/employees/${this.employee.id}/job-mutations`, data).then(resp => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showInfo('Successfully deleted job information.')
                    this.$emit('updated')
                    this.getData()
                }).catch(errors => {
                    this.$store.dispatch('STOP_LOADING')
                    this.$bus.showError(errors.__all__)
                })
            }
        },
        editJob(jobId) {
            const job = this.employee.jobs.find(j => j.id == jobId)
            if (!job) {return}

            const mutations = this.jobMutations.filter(m => m.is_active && (m.job_id == job.id))
            let latestMutation = null

            const sortKey = (m) => {
                return `${m.effective_on}-${m.created_on}-${m.id}`
            }

            for (const m of mutations) {
                if (!latestMutation) {
                    latestMutation = m
                    continue
                }

                if (sortKey(m) >= sortKey(latestMutation)) {
                    latestMutation = m
                }
            }

            if (latestMutation) {
                this.currentMutationId = latestMutation.id
            }
            else {
                this.currentJobId = job.id
            }

            this.editModalOpen = true
        },
        getData(silent) {
            if (!silent) { this.$store.dispatch('START_LOADING') }
            return this.$api.get(`/clients/${this.client.id}/employees/${this.employee.id}/job-mutations`).then(resp => {
                this.$store.dispatch('STOP_LOADING')
                this.jobMutations = resp
            }).catch(errors => {
                this.$store.dispatch('STOP_LOADING')
            })
        },
        computeDiff(cur, old) {
            const fields = [
                'pay_type_pretty',
                'hourly_rate',
                'tip_credit_rate',
                'salary_rate',
                'default_hours',
            ]

            const diff = {}
            for (const field of fields) {
                const curVal = cur[field] ? cur[field] : 'N/A'
                const oldVal = old[field] ? old[field] : 'N/A'

                if (curVal != oldVal) {
                    diff[field] = {old: oldVal, cur: curVal}
                }
            }

            function computeACDiff(cur, old) {
                const curMap = new Map()
                const oldMap = new Map()

                cur.forEach(item => {
                    curMap.set(item.earning_type_slug, item.rate)
                })

                old.forEach(item => {
                    oldMap.set(item.earning_type_slug, item.rate)
                })

                const result = []

                // Find added and updated items
                curMap.forEach((rate, slug) => {
                    if (!oldMap.has(slug)) {
                        // Item was added
                        result.push({
                            added: {
                                earning_type_slug: slug,
                                ...(rate !== undefined && { rate })
                            }
                        })
                    } else if (oldMap.get(slug) !== rate) {
                        // Item was updated (rate changed)
                        result.push({
                            updated: {
                                earning_type_slug: slug,
                                old: oldMap.get(slug),
                                cur: rate
                            }
                        })
                    }
                })

                 // Find removed items
                oldMap.forEach((rate, slug) => {
                    if (!curMap.has(slug)) {
                        result.push({
                            removed: {
                                earning_type_slug: slug,
                                ...(rate !== undefined && { rate })
                            }
                        })
                    }
                })

                return result
            }

            const acDiff = computeACDiff(cur.additional_compensation, old.additional_compensation)
            if (acDiff.length) {
                diff.additional_compensation = acDiff
            }

            return diff
        },
        computeDiffLine(diff) {
            const fieldLabels = {
                'pay_type_pretty': 'Pay Type',
                'hourly_rate': 'Hourly Rate',
                'tip_credit_rate': 'Tip Credit Rate',
                'salary_rate': 'Salary Rate',
                'default_hours': 'Default Hours',
                'additional_compensation': 'Additional Compensation',
            }

            if (Object.keys(diff).length == 0) {
                return 'No changes to be applied.' // This shouldn't happen
            }

            if (Object.keys(diff).length == 1) {
                const old = Object.values(diff)[0].old
                const cur = Object.values(diff)[0].cur

                if (diff.additional_compensation) {
                    return 'Additional Compensation changed'
                }
                else if (diff.pay_type_pretty) {
                    return `Pay Type changed from ${old} to ${cur}`
                }
                else if (diff.hourly_rate) {
                    return `Hourly Rate changed from ${this.$filters.dollars(old)} to ${this.$filters.dollars(cur)}`
                }
                else if (diff.tip_credit_rate) {
                    return `Tip Credit Rate changed from ${this.$filters.dollars(old)} to ${this.$filters.dollars(cur)}`
                }
                else if (diff.salary_rate) {
                    return `Salary Rate changed from ${this.$filters.dollars(old)} to ${this.$filters.dollars(cur)}`
                }
                else if (diff.default_hours) {
                    return `Default Hours changed from ${old} to ${cur}`
                }
            }

            const labels = []
            for (const field of Object.keys(diff)) {
                const label = fieldLabels[field]
                labels.push(label)
            }

            return `${labels.join(', ')} have been changed`
        },
    },
}
</script>

<style scoped lang="scss">
.pending-change {
    background-color: #e8f4f9;
    color: #0c5460;
    border-radius: 0.25rem;
    border-left: 8px solid #1c69a3;
}
</style>
